import { useState, type CSSProperties } from 'react';
import { Link, Navigate } from 'react-router';
import type { ReceivedItemDto } from '@tiffinify/contracts';
import { formatMonth, formatShort } from '@tiffinify/core';
import { Avatar, Badge, BottomNav, Card, CardTitle, Chip, Chips, Empty, EmptyFilterState, Hero, Icon, IconBtn, Mono, MonthNav, PageLoader, PageTitle, RingAvatar, SearchField, Section, TopBar, LoadError } from '../components/ui';
import { formatInr } from '../lib/format';
import { useMe, useRevenue } from '../lib/queries';

/** Rows shown at first, and added by each "Show more". */
const STEP = 10;

type Kind = 'upi' | 'cash' | 'refund';
const kindOf = (x: ReceivedItemDto): Kind => (x.kind === 'refund' ? 'refund' : x.method);
const KIND_LABEL: Record<Kind, string> = { upi: 'UPI', cash: 'Cash', refund: 'Refund' };

/** Every payment and refund of the month, newest first: search, filter by kind, and more on demand. */
function PaymentsHistory({ items }: { items: ReceivedItemDto[] }) {
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<Kind | 'all'>('all');
  const [shown, setShown] = useState(STEP);

  const counts: Record<Kind, number> = { upi: 0, cash: 0, refund: 0 };
  for (const x of items) counts[kindOf(x)]++;
  const q = search.trim().toLowerCase();
  const filtered = items.filter((x) => (kind === 'all' || kindOf(x) === kind) && (!q || x.name.toLowerCase().includes(q) || String(x.amount).includes(q)));
  const reset = (k: Kind | 'all', s = search) => { setKind(k); setSearch(s); setShown(STEP); };

  return (
    <Section title="Payments and refunds" meta={items.length > 0 ? <Badge>{items.length}</Badge> : undefined}>
      {items.length === 0 ? (
        <Card><Empty title="No payments this month">Payments you record show up here.</Empty></Card>
      ) : (
        <Card>
          <div className="pay-history-toolbar">
            {items.length > STEP && <SearchField value={search} onChange={(v) => reset(kind, v)} label="Search payments" placeholder="Name or amount" />}
            <Chips sm scroll label="Show">
              <Chip on={kind === 'all'} count={items.length} onClick={() => reset('all')}>All</Chip>
              {(['upi', 'cash', 'refund'] as const).filter((k) => counts[k] > 0).map((k) => (
                <Chip key={k} on={kind === k} count={counts[k]} onClick={() => reset(k)}>{k === 'refund' ? 'Refunds' : KIND_LABEL[k]}</Chip>
              ))}
            </Chips>
          </div>

          {filtered.length === 0 ? (
            <EmptyFilterState title="No payments match" desc={q ? `Nothing for “${search.trim()}”.` : 'Nothing of this kind this month.'} onReset={() => reset('all', '')} resetLabel="Show all" />
          ) : (
            <div className="pay-history-list">
              {filtered.slice(0, shown).map((x) => (
                <Link key={x.paymentId} to={`/pay/${x.subscriptionId}`} className="pay-record-link">
                  <Avatar name={x.name} size={40} />
                  <span className="pay-record-mid">
                    <span className="pay-record-name">{x.name}</span>
                    <span className="pay-record-meta">
                      <span className={`pay-method-badge ${kindOf(x)}`}>{KIND_LABEL[kindOf(x)]}</span>
                      {formatShort(x.paidOn)}
                    </span>
                  </span>
                  <span className={`pay-amount${x.kind === 'refund' ? ' refund' : ''}`}>{x.kind === 'refund' ? '−' : ''}{formatInr(x.amount)}</span>
                  <Icon name="right" size={15} />
                </Link>
              ))}
            </div>
          )}

          {filtered.length > shown && (
            <button type="button" className="show-more" onClick={() => setShown((n) => n + STEP)}>
              Show {Math.min(STEP, filtered.length - shown)} more{filtered.length - shown > STEP && <small>· {filtered.length - shown} left</small>}
            </button>
          )}
        </Card>
      )}
    </Section>
  );
}

export function Revenue() {
  const [month, setMonth] = useState<string | undefined>(undefined);
  const me = useMe();
  const isOwner = me.data?.user.role === 'owner';
  const q = useRevenue(month, isOwner);
  const r = q.data;

  if (me.data && !isOwner) return <Navigate to="/" replace />;

  // The server defaults to this month too, so the header is complete before the numbers arrive.
  const currentMonth = me.data?.today.slice(0, 7) ?? new Date().toISOString().slice(0, 7);
  const m = month ?? r?.month ?? currentMonth;
  const collected = r ? formatInr(r.collected) : '';

  return (
    <>
      <main className="main">
        <TopBar to="/kitchen" label="Back to kitchen" />
        <PageTitle title="Revenue" sub="Money in, and money still to come" />
        <MonthNav month={m} onChange={setMonth} currentMonth={currentMonth} />
        {q.isError && !r ? (
          <LoadError what="revenue" onRetry={() => void q.refetch()} />
        ) : !r ? (
          <PageLoader label="Adding up payments" />
        ) : (
          <>
            <Hero label={`Money collected in ${formatMonth(r.month)}`}>
              <div className="between"><Mono muted>Collected in {formatMonth(r.month)}</Mono><Mono muted>after refunds</Mono></div>
              <span className="big money" style={{ '--n': collected.length } as CSSProperties}>{collected}</span>
              <div className="hero-facts pair">
                <div className="hero-fact"><Mono muted>Still due</Mono><b className="saffron">{formatInr(r.pending)}</b><small>{r.due.length} {r.due.length === 1 ? 'customer' : 'customers'}</small></div>
                <div className="hero-fact"><Mono muted>Active plans</Mono><b>{r.activeCount}</b><small>running now</small></div>
              </div>
            </Hero>

            {(r.extras.collected > 0 || r.extras.pending > 0) && (
              <Card pad>
                <CardTitle title="Extras" right={r.extras.pending > 0 ? <Badge kind="due">{formatInr(r.extras.pending)} unpaid</Badge> : <Badge kind="ok" icon="check">Paid up</Badge>} />
                <div className="pay-total"><b>{formatInr(r.extras.collected)}</b><span>collected in {formatMonth(r.month)}, on top of plans</span></div>
              </Card>
            )}

            <Section title="Due" meta={<Mono>Tap to record</Mono>}>
              {r.due.length === 0 ? (
                <Card><Empty title="Nothing due">Every plan is paid up.</Empty></Card>
              ) : (
                <Card>
                  {r.due.map((d) => (
                    <div className="person" key={d.subscriptionId}>
                      <Link to={`/pay/${d.subscriptionId}`}>
                        <RingAvatar name={d.name} frac={d.price ? d.paid / d.price : 0} color="var(--due-ring)" />
                        <span className="who">
                          <span className="split"><b>{d.name}</b><b>{formatInr(d.due)}</b></span>
                          <small>{d.paid > 0 ? `${formatInr(d.paid)} paid of ${formatInr(d.price)}` : `Nothing paid yet · ${formatInr(d.price)}`}</small>
                        </span>
                      </Link>
                      <IconBtn icon="chat" label={`Send ${d.name.split(' ')[0]} a payment reminder`} to={`/remind/${d.subscriptionId}?type=payment`} />
                    </div>
                  ))}
                </Card>
              )}
            </Section>

            {/* Keyed by month: a new month starts at the top, unfiltered. */}
            <PaymentsHistory key={r.month} items={r.received} />
          </>
        )}
      </main>
      <BottomNav />
    </>
  );
}
